import { useState, useEffect } from 'react';
import anime from 'animejs';

interface NavigationProps {
  sections: string[];
  activeSection: string;
  onSectionClick: (section: string) => void;
}

export function Navigation({ sections, activeSection, onSectionClick }: NavigationProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Animate navigation items on mount
    anime({
      targets: '.nav-item',
      translateY: [-20, 0],
      opacity: [0, 1],
      duration: 800,
      delay: (_el, i) => i * 100,
      easing: 'easeOutQuart'
    });
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    
    if (!isMenuOpen) {
      anime({
        targets: '.mobile-nav-item',
        translateX: [-100, 0],
        opacity: [0, 1],
        duration: 600,
        delay: (_el, i) => i * 100,
        easing: 'easeOutQuart'
      });
    }
  };

  const handleSectionClick = (section: string) => {
    onSectionClick(section);
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Desktop Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-black/80 backdrop-blur-md border-b border-[var(--synthwave-primary)]/20' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex-shrink-0">
              <span className="text-2xl font-orbitron font-bold neon-text">
                &lt;/AI&gt;
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                {sections.map((section) => (
                  <button
                    key={section}
                    onClick={() => handleSectionClick(section)}
                    className={`nav-item px-3 py-2 text-sm font-rajdhani font-medium uppercase tracking-wider transition-all duration-300 hover:text-[var(--synthwave-neon)] ${
                      activeSection === section
                        ? 'text-[var(--synthwave-neon)] neon-text'
                        : 'text-white/80'
                    }`}
                  >
                    {section}
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={toggleMenu}
                className="inline-flex items-center justify-center p-2 rounded-md text-white hover:text-[var(--synthwave-neon)] focus:outline-none transition-colors duration-300"
              >
                <div className="w-6 h-6 relative">
                  <span className={`absolute block w-full h-0.5 bg-current transform transition-all duration-300 ${
                    isMenuOpen ? 'rotate-45 top-3' : 'top-1'
                  }`} />
                  <span className={`absolute block w-full h-0.5 bg-current transform transition-all duration-300 top-3 ${
                    isMenuOpen ? 'opacity-0' : 'opacity-100'
                  }`} />
                  <span className={`absolute block w-full h-0.5 bg-current transform transition-all duration-300 ${
                    isMenuOpen ? '-rotate-45 top-3' : 'top-5'
                  }`} />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden transition-all duration-300 overflow-hidden ${
          isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-2 pt-2 pb-3 space-y-1 bg-black/90 backdrop-blur-md border-t border-[var(--synthwave-primary)]/20">
            {sections.map((section) => (
              <button
                key={section}
                onClick={() => handleSectionClick(section)}
                className={`mobile-nav-item block w-full text-left px-3 py-2 text-base font-rajdhani font-medium uppercase tracking-wider transition-all duration-300 hover:text-[var(--synthwave-neon)] ${
                  activeSection === section
                    ? 'text-[var(--synthwave-neon)] neon-text'
                    : 'text-white/80'
                }`}
              >
                {section}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Scroll Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-black/20 z-40">
        <div 
          className="h-full bg-gradient-to-r from-[var(--synthwave-primary)] via-[var(--synthwave-secondary)] to-[var(--synthwave-accent)] transition-all duration-300"
          style={{
            width: `${(window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100}%`
          }}
        />
      </div>
    </>
  );
}